import { Fragment, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { decodeValue } from "pages/documents/converters";
import { FieldType, ValidationWrapper } from "pages/documents";
import { DvlaLookupServiceForm } from "pages/documents/VehicleDetails/DvlaLookupServiceForm";
import { ManualLookupServiceFieldset } from "pages/documents/VehicleDetails/ManualLookupServiceFieldset";

import { Field } from "components/Field";
import { Spacer } from "components/Spacer";
import { Input } from "components/Input";
import { Button } from "components/Button";
import { Title } from "components/Title";

import { getValidationErrors } from "./helpers";

export function VehicleDetailsForm({
  isEditPage = false,
  document,
  buttons = [],
  onSave,
  vehicleLookupService = "manual",
  className = "",
}) {
  const { t } = useTranslation();

  const [extraInfo, setExtraInfo] = useState({
    area: "",
  });

  const [vehicleInfo, setVehicleInfo] = useState({
    registration: "",
    year: "",
    make: "",
    model: "",
    colour: "",
    emissions: "",
    nctExpiry: undefined,
    roadTaxExpiry: undefined,
  });

  const [errors, setErrors] = useState({});

  useEffect(
    function syncInternalStateWithDocument() {
      setExtraInfo({
        area: decodeValue(document.area, FieldType.TEXT),
      });

      setVehicleInfo({
        registration: decodeValue(document.registration, FieldType.TEXT),
        year: decodeValue(document.year, FieldType.TEXT),
        make: decodeValue(document.make, FieldType.TEXT),
        model: decodeValue(document.model, FieldType.TEXT),
        colour: decodeValue(document.colour, FieldType.TEXT),
        emissions: decodeValue(document.emissions, FieldType.TEXT),
        nctExpiry: decodeValue(document.nctExpiry, FieldType.DATETIME),
        roadTaxExpiry: decodeValue(document.roadTaxExpiry, FieldType.DATETIME),
      });
    },
    [document, setExtraInfo, setVehicleInfo],
  );

  function onDocumentSave() {
    const form = {
      ...extraInfo,
      ...vehicleInfo,
      vehicleLookupService,
    };

    const errors = getValidationErrors(form, t);
    setErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    onSave(form);
  }

  function onVehicleFieldsetChange(fieldName, fieldValue) {
    setVehicleInfo((prevState) => ({
      ...prevState,
      [fieldName]: fieldValue,
    }));

    setErrors((prevState) => ({
      ...prevState,
      [fieldName]: undefined,
    }));
  }

  const onVehicleDetailsChange = useCallback(
    function (patch) {
      setVehicleInfo((prevState) => ({
        ...prevState,
        ...patch,
      }));
    },
    [setVehicleInfo],
  );

  return (
    <ValidationWrapper className={className}>
      <Title>{t("documentTypeVehicleDetails.name")}</Title>
      {vehicleLookupService === "manual" ? (
        <ManualLookupServiceFieldset
          isEditPage={isEditPage}
          data={vehicleInfo}
          errors={errors}
          onChange={onVehicleFieldsetChange}
        />
      ) : (
        <DvlaLookupServiceForm
          isEditPage={isEditPage}
          data={vehicleInfo}
          errors={errors}
          setErrors={setErrors}
          onVehicleDetailsChange={onVehicleDetailsChange}
          onVehicleFieldsetChange={onVehicleFieldsetChange}
        />
      )}

      <Field label={t("field.preferredArea")} htmlFor="area">
        <Input
          id="area"
          name="area"
          disabled={!isEditPage}
          value={extraInfo.area}
          onChange={(event) => {
            setExtraInfo((prevState) => ({
              ...prevState,
              area: event?.target?.value,
            }));
          }}
        />
      </Field>

      {buttons.map((buttonProps, index) => {
        const { onClick, ...otherProps } = buttonProps;
        const { shape = "round" } = otherProps;

        return (
          <Fragment key={otherProps.children}>
            <Button
              {...otherProps}
              shape={shape}
              onClick={onClick === "onSave" ? onDocumentSave : onClick}
            />
            {index !== buttons.length - 1 ? <Spacer /> : null}
          </Fragment>
        );
      })}
    </ValidationWrapper>
  );
}

